import { Platform, View } from "@-/com.npmjs.react-native";
import pMemoize from "@-/com.npmjs.p-memoize";
import type { ComponentProps, ComponentType, ReactNode } from "react";
import { useAsyncComponent } from "@-/dev.improve.haniverse.lib.use-async-component";

type LineChartComponent = typeof import("react-native-gifted-charts/dist/LineChart").LineChart;
type LineChartProps = ComponentProps<LineChartComponent>;
type DataPointItem = {
  dataPointColor?: string;
  dataPointHeight?: number;
  dataPointRadius?: number;
  dataPointWidth?: number;
};

const DEFAULT_DATA_POINT_SIZE = 4;
const DEFAULT_DATA_POINT_COLOR = "black";

function makeWebDataPoint(props: LineChartProps): (item?: DataPointItem) => ReactNode {
  return (item) => {
    const width =
      item?.dataPointWidth ??
      props.dataPointsWidth1 ??
      props.dataPointsWidth ??
      DEFAULT_DATA_POINT_SIZE;
    const height =
      item?.dataPointHeight ??
      props.dataPointsHeight1 ??
      props.dataPointsHeight ??
      DEFAULT_DATA_POINT_SIZE;
    const color =
      item?.dataPointColor ??
      props.dataPointsColor1 ??
      props.dataPointsColor ??
      DEFAULT_DATA_POINT_COLOR;
    const radius =
      props.dataPointsShape1 === "rectangular" || props.dataPointsShape === "rectangular"
        ? 0
        : (item?.dataPointRadius ?? props.dataPointsRadius1 ?? props.dataPointsRadius ?? width / 2);

    return (
      <View
        style={{
          backgroundColor: color,
          borderRadius: radius,
          height,
          width,
        }}
      />
    );
  };
}

function LineChartWebSafeDataPoints({
  customDataPoint,
  data2,
  data3,
  data4,
  data5,
  dataSet,
  LineChart,
  secondaryData,
  ...props
}: LineChartProps & { LineChart: ComponentType<LineChartProps> }) {
  const hasMultipleSeries =
    data2 !== undefined ||
    data3 !== undefined ||
    data4 !== undefined ||
    data5 !== undefined ||
    dataSet !== undefined ||
    secondaryData !== undefined;

  return (
    <LineChart
      {...props}
      customDataPoint={
        customDataPoint === undefined && !hasMultipleSeries && Platform.OS === "web"
          ? makeWebDataPoint(props)
          : customDataPoint
      }
      data2={data2}
      data3={data3}
      data4={data4}
      data5={data5}
      dataSet={dataSet}
      secondaryData={secondaryData}
    />
  );
}

const loadLineChart = pMemoize(async (): Promise<LineChartComponent> => {
  const { LineChart } = await import("react-native-gifted-charts/dist/LineChart");
  return LineChart;
});

export function LineChart(props: LineChartProps) {
  const Component = useAsyncComponent(loadLineChart);
  return Component === null ? null : (
    <LineChartWebSafeDataPoints LineChart={Component} {...props} />
  );
}
